/**
 * <hold-product-demo>
 *
 * Wraps the Shopify-hosted demonstration video in the HOLD product demo
 * section. It never loops, never carries audio, and never moves unless the
 * visitor asks it to.
 *
 *   At rest          a still image covers the video. That is all a visitor
 *                    who does nothing ever sees.
 *   Mouse            entering the frame plays from the start; leaving it goes
 *                    back to the still.
 *   Touch            a tap plays; a second tap goes back to the still. There
 *                    is no hover to lean on, so the tap carries both.
 *   Keyboard         focusing the frame behaves as entering it, blurring as
 *                    leaving, so the keyboard reaches the same thing.
 *   Ends             if it runs out with the pointer still inside, the last
 *                    frame holds and a "Replay" cue appears. Leaving still
 *                    returns to the still image.
 *   Off screen       scrolling it away mid-play returns it to the still, so it
 *                    never runs where nobody is looking.
 *
 * Nothing autoplays. Muted autoplay is permitted by every current browser, but
 * a demonstration that starts itself is a decision taken away from the
 * visitor, and the exceptions (iOS Low Power Mode, data saver) made it
 * unreliable anyway. Because every entry point is now an explicit action,
 * prefers-reduced-motion is not consulted: it governs motion nobody asked for.
 *
 * The cue is purely visual; the button covering the frame carries the same
 * words as its accessible name ("Play: ..." / "Replay: ...").
 *
 * The video is not fetched until the section approaches the viewport.
 *
 * If the section supplies a mobile video, it replaces the source below 750px
 * before that fetch happens, so only one file is ever downloaded. Without one,
 * the landscape video is shown in full rather than cropped.
 *
 * Follows Dawn's guarded custom-element pattern so a duplicated script tag
 * cannot throw.
 */
if (!customElements.get('hold-product-demo')) {
  customElements.define(
    'hold-product-demo',
    class HoldProductDemo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.control = this.querySelector('.hold-demo__control');
        this.poster = this.querySelector('.hold-demo__poster');

        // Whether hovering means anything here. pointerType alone is not
        // enough: a tap fires pointerenter as well, and not every browser
        // labels it "touch", so the enter would play and the click that
        // follows would immediately toggle it back off - one tap, nothing
        // happens. This asks the device instead of guessing from the event.
        this.canHover = window.matchMedia('(hover: hover)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        // video_tag does not emit these, and Safari refuses inline playback
        // without playsinline.
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.loop = false;
        this.video.controls = false;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');
        this.video.setAttribute('muted', '');

        this.onEnded = this.onEnded.bind(this);
        this.onPointerEnter = this.onPointerEnter.bind(this);
        this.onPointerLeave = this.onPointerLeave.bind(this);
        this.onControlClick = this.onControlClick.bind(this);
        this.onControlFocus = this.onControlFocus.bind(this);
        this.onControlBlur = this.onControlBlur.bind(this);

        this.video.addEventListener('ended', this.onEnded);
        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.control?.addEventListener('click', this.onControlClick);
        this.control?.addEventListener('focus', this.onControlFocus);
        this.control?.addEventListener('blur', this.onControlBlur);

        this.applyMobileSource();
        this.observePreload();
        this.observeViewport();
      }

      disconnectedCallback() {
        clearTimeout(this.rewindTimer);
        this.preloadObserver?.disconnect();
        this.viewportObserver?.disconnect();
        this.video?.removeEventListener('ended', this.onEnded);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.control?.removeEventListener('click', this.onControlClick);
        this.control?.removeEventListener('focus', this.onControlFocus);
        this.control?.removeEventListener('blur', this.onControlBlur);
      }

      /**
       * Swap in the portrait cut on phones, before anything is fetched.
       *
       * A `src` attribute on <video> takes precedence over its <source>
       * children, so setting it here is enough - the landscape sources stay
       * in the markup as the no-JS fallback and are simply ignored. When no
       * mobile video has been supplied this does nothing at all and the
       * landscape video is shown in full, uncropped.
       */
      applyMobileSource() {
        const mobileSrc = this.dataset.mobileSrc;
        if (!mobileSrc) return;
        if (!window.matchMedia('(max-width: 749px)').matches) return;

        this.video.src = mobileSrc;
        if (this.dataset.mobilePoster) {
          // The still image is what a visitor actually sees at rest, so the
          // portrait poster has to replace that, not the video's attribute.
          this.video.poster = this.dataset.mobilePoster;
          if (this.poster) {
            this.poster.removeAttribute('srcset');
            this.poster.removeAttribute('sizes');
            this.poster.src = this.dataset.mobilePoster;
          }
        }
      }

      /** Hold off on fetching the video until the section is nearly in view. */
      observePreload() {
        if (!('IntersectionObserver' in window)) {
          this.video.preload = 'metadata';
          return;
        }

        this.preloadObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.video.preload = 'auto';
            this.video.load();
            this.preloadObserver.disconnect();
          },
          { rootMargin: '400px 0px' }
        );

        this.preloadObserver.observe(this);
      }

      /**
       * Nothing here starts the video any more. The observer's only remaining
       * job is to stop one that is already running from playing on past the
       * bottom of the screen, which can happen when a pointer stays inside the
       * frame while the page scrolls under it.
       */
      observeViewport() {
        if (!('IntersectionObserver' in window)) return;

        this.viewportObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) return;
              if (this.classList.contains('hold-demo--playing')) this.reset();
            });
          },
          { threshold: 0.5 }
        );

        this.viewportObserver.observe(this);
      }

      onPointerEnter(event) {
        if (!this.canHover.matches || event.pointerType === 'touch') return;
        // A pointer crossing the frame on its way elsewhere is not a request,
        // which is the one entry point reduced motion should still veto. Tap,
        // click and Enter are explicit and ignore it; the Play cue is sitting
        // there, so there is always a way in.
        if (this.reducedMotion.matches) return;
        if (this.classList.contains('hold-demo--playing')) return;
        this.play();
      }

      onPointerLeave(event) {
        if (!this.canHover.matches || event.pointerType === 'touch') return;
        this.reset();
      }

      /**
       * On touch this button is the only way in and the only way back, so a
       * tap has to toggle. Where there is a pointer, leaving already returns
       * to the still, so a click on a running video means "again" rather than
       * "stop" - and it is the escape hatch that ignores reduced motion.
       */
      onControlClick() {
        if (!this.canHover.matches && this.classList.contains('hold-demo--playing')) {
          this.reset();
          return;
        }
        this.play();
      }

      /**
       * Keyboard parity: focus is this control's version of entering the
       * frame, and blur of leaving it.
       *
       * :focus-visible is checked so that the focus a mouse click leaves
       * behind does not fight the click's own toggle - tapping to stop it
       * would otherwise focus the button and start it again.
       */
      onControlFocus() {
        let keyboard = true;
        try {
          keyboard = this.control.matches(':focus-visible');
        } catch (error) {
          // An engine that does not know the pseudo-class throws from
          // matches. Treat the focus as a keyboard focus rather than let an
          // exception reach the console.
        }
        if (!keyboard) return;
        if (this.classList.contains('hold-demo--playing')) return;
        this.play();
      }

      onControlBlur() {
        // Not gated on hover: an external keyboard on a tablet needs this too.
        // The guard that matters is whether a pointer is still in the frame,
        // which is what the click-then-focus case leaves behind.
        try {
          if (this.matches(':hover')) return;
        } catch (error) {
          // :hover is universally supported; if matches throws, fall through.
        }
        this.reset();
      }

      onEnded() {
        // With a pointer, this is reachable only while it is still inside the
        // frame, so holding the final frame and offering a replay beats
        // snapping back to the still under a stationary cursor. On touch
        // there is no parked pointer and no hover to leave, so the end is the
        // way back.
        if (!this.canHover.matches) {
          this.reset();
          return;
        }
        this.classList.remove('hold-demo--playing');
        this.classList.add('hold-demo--complete');
        this.updateLabel();
      }

      play() {
        if (!this.video) return;
        clearTimeout(this.rewindTimer);
        this.classList.add('hold-demo--playing');

        try {
          this.video.currentTime = 0;
        } catch (error) {
          // Seeking before metadata has loaded throws in some browsers; the
          // video simply starts wherever it is.
        }

        const played = this.video.play();
        if (played && typeof played.catch === 'function') {
          played.catch(() => {
            // Autoplay was refused. The previous cue (Play or Replay) comes
            // back, so the frame never looks like a broken video.
            this.classList.remove('hold-demo--playing');
            this.updateLabel();
          });
        }
      }

      /**
       * Back to the resting still.
       *
       * The rewind happens while the image is already covering the video, so
       * the jump back to frame zero is never on screen. Removing --playing is
       * what fades the image in; see assets/section-hold-product-demo.css.
       */
      reset() {
        if (!this.video) return;
        this.classList.remove('hold-demo--playing', 'hold-demo--complete');
        this.video.pause();
        this.updateLabel();

        // Rewinding now would be visible through the still's 320ms fade, as a
        // jump to frame one behind a half-transparent image. Freezing where it
        // is looks like nothing at all, so the seek waits until the still has
        // finished covering the video.
        clearTimeout(this.rewindTimer);
        this.rewindTimer = setTimeout(() => {
          try {
            this.video.currentTime = 0;
          } catch (error) {
            // Seeking before metadata has loaded throws in some browsers.
          }
        }, 360);
      }

      updateLabel() {
        if (!this.control) return;
        const complete = this.classList.contains('hold-demo--complete');
        const label = complete ? this.control.dataset.labelReplay : this.control.dataset.labelPlay;
        if (label) this.control.setAttribute('aria-label', label);
      }
    }
  );
}
